<template>
    <div class="border bg-gray-100 dark:bg-gray-800 rounded p-4 mt-3">
        <div class="mb-4">Simple List With Images</div>
        <div class="w-full">
            <Select
                label="Image Position"
                name="image_position"
                v-model="data.image_position"
                class="ml-3"
                :options="[
                    { value: 'top', name: 'Top' },
                    { value: 'left', name: 'Left' },
                ]"
            />

            <input-text
                label="Number of visible items"
                name="number_of_visible_items"
                v-model="data.number_of_visible_items"
                class="ml-3 mt-5"
            />
        </div>

        <draggable
            v-model="data.items"
            ghost-class="bg-gray-500"
            handle=".handle"
            @choose="froalaStore.disableFroala = true"
            @unchoose="froalaStore.disableFroala = false"
        >
            <div v-for="(item, itemIndex) in data.items"
                 :key="`simpleListWithImages-${item.id || item.uuid}`"
                 class="relative mt-5 item mt-10">
                    <div class="flex border-b-2">
                        <div class="mb-3 text-center max-w-1xl">
                            <media-reference
                                :name="`simple_list_with_images_[${itemIndex}][image]`"
                                v-model="item.image"
                                :language="language"
                                class="ml-3"
                                @input="updateImage(itemIndex, $event)"
                            />
                        </div>

                        <template v-for="(row, rowIndex) in item.rows">
                            <div class="ml-20 float-right max-w-1xl w-96">
                                <input-text
                                    name="text"
                                    label="Text"
                                    class="mb-3 mt-5"
                                    v-model="row.text"
                                    :maxlength="255"
                                />

                                <rocket-link
                                    :language="language"
                                    v-model="row.link"
                                    class="mb-3 mt-5"
                                    label="Link"
                                    :resettable="true"
                                />

                                <button @click="deleteSecondText($event, itemIndex, rowIndex)" v-if="item.rows.length == 2 && rowIndex == 1" type="button" class="h-8 transform -translate-y-1/2 hover:bg-red-500 hover:text-red-100 transition duration-75 bg-red-600 text-white rounded shadow ml-44 mt-3">
                                    <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <button
                                class="mt-6 ml-10 text-indigo-600 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-200"
                                type="button"
                                @click="addSecondText($event, itemIndex, rowIndex)"
                                v-if="item.rows.length < 2"
                            >
                                + Add Second Text Option
                            </button>
                        </template>
                        <div
                            v-if="data.items.length > 1"
                            @click="handleDelete(itemIndex)"
                            class="ml-10 cursor-pointer mt-auto mb-auto"
                        >
                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </div>
                        <div class="ml-4 handle mt-auto mb-auto" title="Drag to reorder items" v-if="data.items.length > 1">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 17l-4 4m0 0l-4-4m4 4V3" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                            </svg>
                        </div>
                    </div>
                </div>

                <button slot="footer"
                    class="mt-6 text-indigo-600 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-200"
                    type="button"
                    @click="addItem"
                    v-if="data.items.length < maxItems"
                >
                    + Add item
                </button>
        </draggable>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import siteLanguage from '../../config/siteLanguage'
import RocketLink from "../Link/RocketLink.vue";
import {useFroalaStore} from "../../stores/froala";
import {v4 as uuidv4} from "uuid";

export default {
    components: {RocketLink, draggable },
    name: "SimpleListWithImages",
    props: {
        value: [Object, Array],
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
        maxItems: {
            type: Number,
            default: 20,
            required: false
        }
    },
    data() {
        return {
            data: {
                image_position: this.value?.image_position  ?? 'left',
                number_of_visible_items: this.value?.number_of_visible_items ?? null,
                items: this.value?.items?.length ? this.value.items : [this.defaultItem()],
            },
            froalaStore: useFroalaStore(),
        };
    },
    updated() {
        this.$emit("input", this.data);
    },
    methods: {
        defaultItem() {
            return {
                uuid: uuidv4(),
                image: null,
                image_id: null,
                rows: [this.defaultRow()]
            }
        },
        defaultRow() {
            return {
                text: null,
                link: {},
            }
        },
        addItem(e) {
            e.preventDefault();
            this.data.items.push(this.defaultItem());
        },
        addSecondText(e, itemIndex, rowIndex) {
            e.preventDefault();
            this.$set(this.data.items[itemIndex].rows, rowIndex+1, this.defaultRow());
        },
        deleteSecondText(e, itemIndex, rowIndex) {
            if (confirm('Are you sure you want to remove this item?')) {
                this.data.items[itemIndex].rows.splice(rowIndex, 1);
            }
        },
        textTypeChanged(row) {
            row.text = null;
        },
        handleDelete: function (indexOfItemToDelete) {
            if (confirm('Are you sure you want to remove this item?')) {
                this.data.items.splice(indexOfItemToDelete, 1);
            }
        },
        updateImage: function (itemIndex, image) {
            this.$set(this.data.items, itemIndex, Object.assign(this.data.items[itemIndex], {image_id: (image && image.id) ? image.id : null}));
        },
    },
    watch: {
        value: function (newValue) {
            this.data = newValue;
        }
    }
};
</script>
